import { formatVisitGrade, isWithinVisitGradeNow, VisitDaySchedule } from '../components/VisitGradeInput';

/**
 * "Liberações" — access a RESIDENT granted in advance, from the app, for their own unit.
 *
 * There is no liberacoes table: a liberação is a person_roles row whose role_metadata carries the
 * unit it applies to, how long it lasts, and whether the porteiro has to interfonar on arrival. The
 * same person can hold several at once — one per unit — with different rules in each, which is
 * precisely what the portaria needs to see before deciding how to receive them.
 *
 * Display only. Nothing here grants or denies anything; the device/role rules do that.
 */

export type LiberacaoKind = 'always' | 'grade' | 'period' | 'single';

/**
 * The server's computed clearance — see models/liberacao.go. One answer for every screen.
 *
 * Optional: against a backend that predates it the field is absent and the local derivation below
 * runs instead. That fallback is scheduled for deletion once every environment is on the new
 * payload — at which point this file stops deciding anything and only formats labels.
 */
export interface ServerLiberacao {
  status: 'liberado' | 'aguardando' | 'encerrada' | 'revogada' | 'vencida';
  kind: 'periodo' | 'grade' | 'sempre' | 'visita_unica';
  unit_id?: string;
  unit_label?: string;
  from?: string;
  until?: string;
  requires_intercom?: boolean;
  revoked_at?: string;
}

const KIND_FROM_SERVER: Record<ServerLiberacao['kind'], LiberacaoKind> = {
  sempre: 'always',
  grade: 'grade',
  periodo: 'period',
  visita_unica: 'single',
};

export interface LiberacaoSummary {
  roleId: string;
  unitId?: string;
  unitLabel: string;
  /**
   * "Av. Carlos Gomes, 3" — the address the porteiro actually directs someone by, when the
   * condomínio uses ruas. Absent for a tower block, which is addressed by bloco/unidade alone.
   *
   * Separate from [unitLabel] rather than folded into it: both are needed. The street sends the
   * visitor down the right road, the quadra/lote identifies the house, and every lot 5 in the
   * condo reads the same without the former.
   */
  unitStreetLine?: string;
  kind: LiberacaoKind;
  /** What the liberação allows: "Liberação permanente", the grade's window, or "Liberação única". */
  periodLabel: string;
  /** How long it lasts: "01/10/2026 até 31/12/2026", "Até 06/10/2026", or "" when open-ended. */
  rangeLabel: string;
  /** false = entrada direta. Absent in the data means true, as everywhere else in the product. */
  requiresIntercom: boolean;
  /** Only for a grade: whether this exact moment falls in an allowed window. */
  withinWindowNow?: boolean;
  /** Liberado, but the period starts in the future — must not read as "liberado agora". */
  notStartedYet: boolean;
  isServiceProvider: boolean;
  /** Who granted it, when the metadata says — the resident, or the operator who set it up. */
  authorizedByName?: string;
}

/**
 * Taken away, run out, or a single visit already used up.
 *
 * "exited" is deliberately absent. The backend decides at check-out whether the liberação survives
 * the visit: an ordinary visit is deactivated (is_active=false) while a período / sempre habilitado
 * role is explicitly left active so the person can come back — and `is_active` is already checked
 * below. Listing "exited" here as well is what made a visitor cleared until 29/10/2026 vanish from
 * "Liberados" the moment the porteiro registered their first saída.
 */
const DEAD_STATES = new Set(['consumed', 'expired', 'revoked', 'denied']);

const parseDate = (value: any): Date | null => {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * A liberação period is a CALENDAR date the resident picked in a date picker, sent as midnight UTC
 * ("2026-12-31T00:00:00Z"). Rendered with the local clock in BRT (UTC-3) that becomes 30/12 — the
 * porteiro would be told the liberação ends a day before the resident said it does. So a value that
 * sits exactly on midnight UTC is read back as the date it encodes; anything with a real time of
 * day (an entry window, an expiry stamped by the backend) stays local.
 */
/**
 * Format a liberação date the way the resident picked it.
 *
 * Exported because the person detail modal was formatting the SAME role with
 * `new Date(x).toLocaleDateString('pt-BR')` and showing "14/10/2026 até 22/10/2026" while the unit
 * page showed "15/10/2026 até 23/10/2026" — one day earlier, for the reason below. Two screens
 * disagreeing about when someone is allowed in is worse than either being wrong.
 */
export const formatLiberacaoDate = (value: string | Date | null | undefined): string => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return formatBR(d);
};

const formatBR = (d: Date): string => {
  const dateOnly =
    d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0;
  const [day, month, year] = dateOnly
    ? [d.getUTCDate(), d.getUTCMonth() + 1, d.getUTCFullYear()]
    : [d.getDate(), d.getMonth() + 1, d.getFullYear()];
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
};

/** An hour the operator typed, read on the local clock — it is a moment, never a calendar date. */
const formatHM = (d: Date): string =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const sameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const scheduleOf = (meta: any): VisitDaySchedule[] | null => {
  const raw = meta?.visit_schedule;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const days = raw.filter((d: any) => d && typeof d.day === 'number' && Array.isArray(d.windows) && d.windows.length > 0);
  return days.length > 0 ? (days as VisitDaySchedule[]) : null;
};

const unitLabelOf = (meta: any): string => {
  const display = (meta?.unit_display || '').toString().trim();
  if (display) return display;
  const pieces = [meta?.block, meta?.unit].map((p) => (p == null ? '' : String(p).trim())).filter(Boolean);
  if (pieces.length > 0) return pieces.join(' ');
  // Resolved server-side: the people list looks every role's unit_id up in the units table and
  // writes the answer back as block_name / unit_number (ListPeople's visitorLookups). A liberação
  // created without unit_display — an import, an older client — carries only these, and reading
  // just unit_display printed "Unidade não informada" beside a unit the payload knew by number.
  const resolved = [meta?.block_name, meta?.unit_number].map((p) => (p == null ? '' : String(p).trim())).filter(Boolean);
  if (resolved.length > 0) return resolved.join(' ');
  return 'Unidade não informada';
};

/**
 * The rua (and número) a liberação points at, when the condomínio has them.
 *
 * Read from the structured fields rather than the frozen `unit_display`, which several creation
 * paths — the resident's own "liberar visita" among them — build from block + number alone.
 * `unit_info` is where a server-resolved role carries them.
 */
/** A unit as the screens that call this already have it loaded. */
export interface ResolvableUnit {
  street?: { name?: string };
  street_name?: string;
  street_number?: string;
  /** A real unit carries far more than this; only the address is read here. */
  [key: string]: unknown;
}

export type ResolveUnit = (unitId: string) => ResolvableUnit | undefined | null;

export interface LiberacaoOptions {
  /**
   * Looks a unit id up among the units the caller has loaded, so the address can be filled in
   * when the role itself does not carry it. Optional: without it, behaviour is exactly as before.
   */
  resolveUnit?: ResolveUnit;
}

const unitStreetLineOf = (meta: any, resolveUnit?: ResolveUnit): string | undefined => {
  const info = meta?.unit_info;
  // The payload first — when the backend enriched the role, that is the authoritative answer.
  let name = (info?.street_name || meta?.street_name || '').toString().trim();
  let number = (info?.street_number || meta?.street_number || '').toString().trim();

  // Otherwise ask the caller, which usually has the whole units list loaded already. This is not a
  // nicety: GET /people/:id/roles — what the Cadastrar Acesso banner reads — returns role_metadata
  // exactly as stored, with none of the enrichment the people list performs, so a liberação made
  // from the resident's app carries no street at all.
  if (!name && resolveUnit && meta?.unit_id) {
    const unit = resolveUnit(String(meta.unit_id));
    name = (unit?.street?.name || unit?.street_name || '').toString().trim();
    number = (unit?.street_number || '').toString().trim();
  }

  if (!name) return undefined;
  return number ? `${name}, ${number}` : name;
};

/**
 * One liberação that has ENDED — the same summary plus when, and why.
 *
 * A history row answers a different question from a live one ("quem já esteve liberado?"), and the
 * two extra fields are the whole difference: a revoke is somebody's decision and a period running
 * out is the calendar, and they read differently to whoever is looking.
 */
export interface LiberacaoHistoricoRow extends LiberacaoSummary {
  /** vencida | revogada | encerrada */
  status: 'vencida' | 'revogada' | 'encerrada';
  /** When it stopped applying, ISO, when the payload says. */
  endedAt?: string;
  /** "Revogada", "Venceu", "Encerrada" — the word a person reads. */
  endedLabel: string;
}

const ENDED_LABELS: Record<LiberacaoHistoricoRow['status'], string> = {
  vencida: 'Venceu',
  revogada: 'Revogada',
  encerrada: 'Encerrada',
};

/**
 * The clearances a set of roles USED to grant.
 *
 * The exact complement of [summarizeLiberacoes]: same "was this arranged in advance?" predicate —
 * an ordinary walk-in whose visit finished is not a liberação that ended, and the portaria
 * deactivates those at check-out too — with the live/ended test inverted. Most recent first.
 *
 * Mirrors the server's `?liberado=history` (handlers/liberado_filter.go), which narrows the PAGE;
 * this describes what came back. See liberacoesHistorico.test.ts.
 */
export function historicoLiberacoes(
  roles: any[] | null | undefined,
  now: Date = new Date()
): LiberacaoHistoricoRow[] {
  const rows = collectLiberacoes(roles, now, 'ended') as LiberacaoHistoricoRow[];
  // Most recent first: a long-lived unit's history is read from the top.
  return rows.sort((a, b) => {
    const left = a.endedAt ? new Date(a.endedAt).getTime() : 0;
    const right = b.endedAt ? new Date(b.endedAt).getTime() : 0;
    return right - left;
  });
}

/** liberado | aguardando | vencida | revogada | encerrada — the five words the server also uses. */
export type LiberacaoRoleStatus = 'liberado' | 'aguardando' | 'vencida' | 'revogada' | 'encerrada';

/**
 * What ONE clearance is right now — or null when the role is not a clearance at all.
 *
 * For the badge on a single card (the person modal), where the two lists above are overkill but
 * their JUDGEMENT is exactly what is wanted. Asking the same walk is the point: the modal used to
 * print `role.liberacao.status` straight from the server and so said "LIBERADO" about a
 * pré-cadastro from 29/08 that the Liberados list on the same screen had already dropped — the
 * list applies "a visita única is over when its day is over" locally, because a one-off usually
 * carries no valid_until for the server to expire it on.
 *
 * Null for an ordinary walk-in: a badge claiming a clearance nobody granted is worse than the
 * caller's own fallback.
 */
export function liberacaoStatusForRole(role: any, now: Date = new Date()): LiberacaoRoleStatus | null {
  const summary = liberacaoSummaryForRole(role, now);
  if (!summary) return null;
  // Only an ENDED summary carries a status; anything else came back from the live walk.
  if ((summary as LiberacaoHistoricoRow).status) return (summary as LiberacaoHistoricoRow).status;
  // A visita única in the live set is by definition unused — once the portaria releases the
  // visitor the role stops being one — and a clearance whose first day is still ahead has not
  // begun. Both are "arranged", not "come and go"; the server says aguardando for the same rows.
  return summary.kind === 'single' || summary.notStartedYet ? 'aguardando' : 'liberado';
}

/**
 * ONE role, described the way the lists describe it — or null when it is not a clearance.
 *
 * The sentence a card prints ("Dia 10/10/2026 às 14:30", "10/10 até 20/10", "Liberação
 * permanente") is composed here and nowhere else. A caller that builds its own from valid_from /
 * valid_until gets a visita única wrong every time: a one-off is an APPOINTMENT, the hour lives
 * in `expected_arrival_at`, and "A partir de 10/10/2026" tells the porteiro the visitor may come
 * any day from then on — which is the opposite of what the resident arranged.
 */
export function liberacaoSummaryForRole(
  role: any,
  now: Date = new Date(),
  options?: LiberacaoOptions,
): LiberacaoSummary | null {
  const [live] = collectLiberacoes([role], now, 'live', options);
  if (live) return live;
  const [ended] = collectLiberacoes([role], now, 'ended', options);
  return ended ?? null;
}

export function summarizeLiberacoes(
  roles: any[] | null | undefined,
  now: Date = new Date(),
  options?: LiberacaoOptions,
): LiberacaoSummary[] {
  return collectLiberacoes(roles, now, 'live', options)
    .sort((a, b) => a.unitLabel.localeCompare(b.unitLabel, 'pt-BR'));
}

/**
 * The one walk over the roles, for both questions.
 *
 * Shared rather than duplicated because the expensive part — deciding whether a role is a
 * liberação at all, and what it says — is identical; only the live/ended test differs. Two copies
 * of this is how "liberado" came to mean six things in the first place (see the header above).
 */
/** The last instant of a day — a named day is over when the day itself is. */
/**
 * The DAY a one-off is for — or null when the role genuinely spans several.
 *
 * A visita única is an APPOINTMENT, not a window: one day the resident named. "A partir de
 * 16/10/2026" says the opposite of what was agreed — that the visitor may turn up any time from
 * then on — and a bare date reads as an expiry.
 *
 * `expected_arrival_at` is what the resident chose; valid_from is the only date a pré-cadastro
 * usually carries; valid_until covers the liberar-visita form, which writes a date instead.
 */
function appointmentDayOf(role: any): Date | null {
  const meta = role?.role_metadata || {};
  const expectedArrival = parseDate(meta.expected_arrival_at);
  const validFrom = parseDate(role?.valid_from);
  const validUntil = parseDate(role?.valid_until);
  // A window that really covers several days is a range and still reads as one.
  const spansDays = !!validFrom && !!validUntil && !sameCalendarDay(validFrom, validUntil);
  if (spansDays && !expectedArrival) return null;
  return expectedArrival || validFrom || validUntil;
}

/** "Dia 16/10/2026 às 14:42" — the hour only when the resident actually gave one. */
function appointmentLabel(day: Date, expectedArrival: Date | null): string {
  return `Dia ${formatBR(day)}` + (expectedArrival ? ` às ${formatHM(expectedArrival)}` : '');
}

/**
 * How a ONE-OFF role reads, for a caller that already knows it is one.
 *
 * The shared walk stops recognising a visita única the moment the portaria releases the visitor —
 * `visitor_state` leaves `pre_registered` and nothing durable is left on the row (a pinned
 * limitation; models/liberacao.go says the same). A card looking at that role still knows what it
 * is, from `access_type`, and must not fall back to a generic range: the person modal said
 * "A partir de 16/10/2026" about a visitor who was inside the condomínio at that moment.
 *
 * Null when the role covers a real span of days, so the caller keeps its range.
 */
export function liberacaoAppointmentLabel(role: any): string | null {
  const day = appointmentDayOf(role);
  if (!day) return null;
  return appointmentLabel(day, parseDate(role?.role_metadata?.expected_arrival_at));
}

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/**
 * What kind of clearance this is, and how to say it. Resolved in one place because the
 * live/ended test now needs it too: a visita única expires on its own day.
 */
function kindOf(input: {
  server: ServerLiberacao | undefined;
  schedule: ReturnType<typeof scheduleOf>;
  alwaysEnabled: boolean;
  customPeriod: boolean;
  standing: boolean;
}): { kind: LiberacaoKind; periodLabel: string } {
  const { server, schedule, alwaysEnabled, customPeriod, standing } = input;
  if (server && KIND_FROM_SERVER[server.kind]) {
    const kind = KIND_FROM_SERVER[server.kind];
    return {
      kind,
      periodLabel:
        kind === 'always' ? 'Liberação permanente'
          : kind === 'grade' && schedule ? formatVisitGrade(schedule)
            : kind === 'period' ? 'Liberação por período'
              : 'Liberação única',
    };
  }
  if (alwaysEnabled) return { kind: 'always', periodLabel: 'Liberação permanente' };
  if (schedule) return { kind: 'grade', periodLabel: formatVisitGrade(schedule) };
  if (customPeriod || standing) return { kind: 'period', periodLabel: 'Liberação por período' };
  return { kind: 'single', periodLabel: 'Liberação única' };
}

function collectLiberacoes(
  roles: any[] | null | undefined,
  now: Date,
  want: 'live' | 'ended',
  options?: LiberacaoOptions,
): LiberacaoSummary[] {
  if (!Array.isArray(roles)) return [];

  const summaries: LiberacaoSummary[] = [];

  for (const role of roles) {
    const meta = role?.role_metadata || {};
    if (role?.role_type !== 'visitor' && role?.role_type !== 'service_provider') continue;
    // NOT gated on is_authorized: a liberação is a mark recorded ahead of time, and both the
    // resident app and the portaria create it unauthorised — authorisation happens at the gate when
    // the person arrives. Requiring it here hid every liberação the app has ever created.
    const state = String(meta.visitor_state || '');
    const deactivated = role?.is_active === false;
    const deadState = DEAD_STATES.has(state);

    const validFrom = parseDate(role.valid_from);
    const validUntil = parseDate(role.valid_until);
    const expired = !!validUntil && validUntil.getTime() < now.getTime();

    const server: ServerLiberacao | undefined = role?.liberacao;
    // Has this clearance ended?
    //
    // When the server has spoken its status IS the answer — it already applied valid_until,
    // is_active and the standing-clearance rule, and a standing liberação can outlive a date the
    // row happens to carry. Without it the three local signals say the same thing, because a
    // backend that predates the field must not make the screen look empty.
    const schedule = scheduleOf(meta);
    const alwaysEnabled = meta.always_enabled === true;
    const customPeriod = meta.custom_period_access === true;
    // An ordinary walk-in registered at the portaria is an active authorized visitor role too. Only
    // access a resident arranged BEFOREHAND may claim "liberado".
    const preRegistered = meta.visitor_state === 'pre_registered';
    // "Válido até <data futura>" from the portaria's own form, which writes no flag: standing
    // because the clearance outlives the day it was created. A walk-in valid until 23:59 today is
    // just today's visit. Mirrors PersonRole.IsStandingAuthorization on the server.
    const standing =
      meta.access_type !== 'single_visit' &&
      (() => {
        const until = parseDate(role.valid_until);
        const created = parseDate(role.created_at);
        if (!until || !created) return false;
        return until.getTime() - created.getTime() > 24 * 60 * 60 * 1000;
      })();
    // Only when the server has not spoken: these are the six-way-duplicated predicates this
    // whole change exists to retire.
    if (!server && !alwaysEnabled && !schedule && !customPeriod && !preRegistered && !standing) continue;

    // A visita única whose day has passed is over, whatever the server still says. The server's
    // status is authoritative for every other kind — it applies valid_until, is_active and the
    // standing rule — but a role for a named day often carries no valid_until at all, and with
    // nothing to expire on it sat in LIBERADOS forever: the porteiro kept being told a visit from
    // last week was cleared to arrive. A date that has already passed is not a judgement call.
    const singleKind = kindOf({ server, schedule, alwaysEnabled, customPeriod, standing }).kind === 'single';
    // The LAST moment it covers: a visit cleared 08→10 October is not over on the 9th.
    const appointmentMoment = validUntil || parseDate(meta.expected_arrival_at) || validFrom;
    const appointmentPassed =
      singleKind && !!appointmentMoment && endOfDay(appointmentMoment).getTime() < now.getTime();

    const ended = appointmentPassed || (server
      ? server.status !== 'liberado' && server.status !== 'aguardando'
      : deactivated || deadState || expired);

    if (want === 'live' && ended) continue;
    if (want === 'ended' && !ended) continue;

    // A clearance can only appear in the HISTORY if something durable says somebody arranged it
    // in advance.
    //
    // `standing` ("válido até mais de 24h") is right for the live question — the portaria's own
    // form creates real clearances that way and writes no flag — and worthless once the role is
    // deactivated at check-out, when nothing tells it apart from an ordinary visit with a
    // generous end date. A unit's first Histórico was eight rows of finished deliveries,
    // including one for a visitor who was inside the condomínio at that moment. Mirrors the
    // server's liberacaoShapeSQL(includeStanding=false).
    if (want === 'ended' && !alwaysEnabled && !schedule && !customPeriod && !preRegistered) continue;

    // A period in the past is the clearest "it ended" there is, and for a payload with no server
    // state it is the only signal — already folded into `ended` above. This keeps the live list
    // honest when the server HAS spoken but the date has since passed between two requests.
    if (want === 'live' && expired && !server) continue;

    const { kind, periodLabel } = kindOf({ server, schedule, alwaysEnabled, customPeriod, standing });

    // A visita única is an APPOINTMENT, not a window: one day the resident named. "Dia 08/10/2026
    // às 14:30" is what they told the portaria; rendering it as a range says less and hides the
    // hour (expected_arrival_at), and rendering it as "A partir de 08/10/2026" says the opposite
    // of what was agreed — that the visitor may turn up any time from then on.
    const expectedArrival = parseDate(meta.expected_arrival_at);
    const appointmentDay = kind === 'single' ? appointmentDayOf(role) : null;

    let rangeLabel = '';
    if (appointmentDay) {
      rangeLabel = appointmentLabel(appointmentDay, expectedArrival);
    } else if (validFrom && validUntil) rangeLabel = `${formatBR(validFrom)} até ${formatBR(validUntil)}`;
    else if (validUntil) rangeLabel = `Até ${formatBR(validUntil)}`;
    else if (validFrom) rangeLabel = `A partir de ${formatBR(validFrom)}`;

    // Why it ended, for the history rows. A revoke is somebody's decision, a period running out is
    // the calendar, and a deactivated standing clearance is neither — they read differently.
    const endedStatus: LiberacaoHistoricoRow['status'] | undefined =
      want !== 'ended'
        ? undefined
        : server && (server.status === 'vencida' || server.status === 'revogada' || server.status === 'encerrada')
          ? server.status
          : state === 'revoked'
            ? 'revogada'
            : expired || appointmentPassed
              ? 'vencida'
              : 'encerrada';
    const endedAt =
      endedStatus === 'revogada'
        ? server?.revoked_at || (typeof meta.revoked_at === 'string' ? meta.revoked_at : undefined)
        : server?.until || (validUntil ? validUntil.toISOString() : undefined);

    summaries.push({
      roleId: role.id,
      unitId: meta.unit_id,
      unitLabel: unitLabelOf(meta),
      unitStreetLine: unitStreetLineOf(meta, options?.resolveUnit),
      kind,
      periodLabel,
      rangeLabel,
      ...(endedStatus
        ? { status: endedStatus, endedAt, endedLabel: ENDED_LABELS[endedStatus] }
        : {}),
      requiresIntercom: meta.requires_intercom !== false,
      withinWindowNow: schedule ? isWithinVisitGradeNow(schedule, now) : undefined,
      notStartedYet: !!validFrom && validFrom.getTime() > now.getTime(),
      isServiceProvider: meta.category === 'service_provider' || role.role_type === 'service_provider',
      authorizedByName: (meta.authorized_by_name || meta.created_by_name || '').toString().trim() || undefined,
    });
  }

  return summaries;
}

/** One liberação, with the person it belongs to — a row in Cadastros → Visitantes → Liberados. */
export interface LiberacaoRow extends LiberacaoSummary {
  /** The people-list item, so the card can reuse the usual photo/vehicle/contact helpers. */
  person: any;
  personId: string;
  personName: string;
  /** Unique per (person, liberação): a person holds one row per unit. */
  rowKey: string;
}

export interface LiberacoesPage {
  rows: LiberacaoRow[];
  /**
   * People the server returned as liberados whose roles say nothing resembling a liberação.
   *
   * Kept instead of dropped on purpose: the server decides WHO is liberado (?liberado=true) and
   * this file decides what each row SAYS. When the two disagree, a list that claims to be complete
   * must say so rather than quietly showing fewer people than the count above it.
   */
  unmatched: any[];
  /**
   * People whose clearances were read perfectly well and have simply ENDED.
   *
   * Separate from [unmatched] because they are not a data problem to go and look at: the server's
   * filter cannot see this case. A visita única carries the day it was for and usually no
   * valid_until at all, so no SQL expires it — the person keeps arriving in ?liberado=true long
   * after the day passed, and gets dropped here. Calling that "sem liberação legível" sent the
   * portaria to open twenty cadastros that had nothing wrong with them.
   */
  ended: any[];
}

const personNameOf = (person: any): string =>
  `${person?.first_name || ''} ${person?.last_name || ''}`.trim() || 'Sem nome';

/**
 * Turn a page of people into a list of liberações — one row per person PER UNIT.
 *
 * "Liberados" is a list of clearances, not of people: the same visitor can be liberado for três
 * unidades at once, each with its own period, grade and intercom rule. Rendered one card per
 * person it showed whichever unit the newest role happened to name and no period at all, so a
 * multi-unit liberação was indistinguishable from a single one — and which unit it named changed
 * with the last visit registered at the gate.
 */
/** One ended liberação, with the person it belonged to — a row in a Histórico list. */
export interface LiberacaoHistoricoPageRow extends LiberacaoHistoricoRow {
  person: any;
  personId: string;
  personName: string;
  rowKey: string;
}

export interface LiberacoesHistoricoPage {
  rows: LiberacaoHistoricoPageRow[];
  /** See LiberacoesPage.unmatched — the same contract, for the same reason. */
  unmatched: any[];
}

/**
 * Turn a page of people into the clearances they USED to hold — one row per person per clearance.
 *
 * The twin of [liberacoesForPeople]: a visitor cleared for três unidades has three endings, not
 * one, and collapsing them would show whichever role happened to come first. Ordered most
 * recently ended first, because that is how a history is read.
 */
export function historicoForPeople(
  people: any[] | null | undefined,
  now: Date = new Date()
): LiberacoesHistoricoPage {
  if (!Array.isArray(people)) return { rows: [], unmatched: [] };

  const rows: LiberacaoHistoricoPageRow[] = [];
  const unmatched: any[] = [];

  for (const person of people) {
    const ended = historicoLiberacoes(person?.roles, now);
    if (ended.length === 0) {
      unmatched.push(person);
      continue;
    }
    const personId = person?.id ? String(person.id) : '';
    const personName = personNameOf(person);
    for (const row of ended) {
      rows.push({
        ...row,
        person,
        personId,
        personName,
        rowKey: `${personId}:${row.roleId || row.unitId || row.unitLabel}`,
      });
    }
  }

  return {
    rows: rows.sort((a, b) => {
      const left = a.endedAt ? new Date(a.endedAt).getTime() : 0;
      const right = b.endedAt ? new Date(b.endedAt).getTime() : 0;
      // Most recent first; same-day endings fall back to the name so the order is stable.
      return right !== left ? right - left : a.personName.localeCompare(b.personName, 'pt-BR');
    }),
    unmatched,
  };
}

export function liberacoesForPeople(people: any[] | null | undefined, now: Date = new Date()): LiberacoesPage {
  if (!Array.isArray(people)) return { rows: [], unmatched: [], ended: [] };

  const rows: LiberacaoRow[] = [];
  const unmatched: any[] = [];
  const ended: any[] = [];

  for (const person of people) {
    const summaries = summarizeLiberacoes(person?.roles, now);
    if (summaries.length === 0) {
      // Over, or unreadable? Asking the history half of the same walk is the only way to tell,
      // and the difference is what the screen tells the operator to do about it.
      if (historicoLiberacoes(person?.roles, now).length > 0) ended.push(person);
      else unmatched.push(person);
      continue;
    }
    const personId = person?.id ? String(person.id) : '';
    const personName = personNameOf(person);
    for (const summary of summaries) {
      rows.push({
        ...summary,
        person,
        personId,
        personName,
        rowKey: `${personId}:${summary.roleId || summary.unitId || summary.unitLabel}`,
      });
    }
  }

  // By name, then unit. The people query itself sorts by newest role, which reorders the list
  // every time someone enters the condo — unusable for a list the operator scans by name.
  rows.sort((a, b) => {
    const byName = a.personName.localeCompare(b.personName, 'pt-BR');
    return byName !== 0 ? byName : a.unitLabel.localeCompare(b.unitLabel, 'pt-BR');
  });

  return { rows, unmatched, ended };
}
